import type { VideoEditorTTSVoice } from '@/shared/apis/tts/get-tts-voices';

export const DEFAULT_TTS_CATALOG_USE_CASE = 'tts.default' as const;

export interface PickDefaultTtsCatalogVoiceOptions {
  vendor?: string | null;
  excludeCode?: string | null;
  preferredCodes?: readonly string[];
  useCase?: string | null;
  allowLegacyOrderFallback?: boolean;
}

export function pickRecommendedTtsCatalogVoice(
  voices: readonly VideoEditorTTSVoice[],
  options?: Pick<
    PickDefaultTtsCatalogVoiceOptions,
    'vendor' | 'excludeCode'
  > & {
    recommendedVoiceCodes?: readonly string[];
  },
): VideoEditorTTSVoice | null {
  const excluded = options?.excludeCode?.trim();
  if (options?.recommendedVoiceCodes) {
    const candidatesByCode = new Map(
      voices
        .filter((voice) => {
          if (options.vendor && voice.vendor !== options.vendor) return false;
          return !excluded || voice.code !== excluded;
        })
        .map((voice) => [normalizeVoiceCode(voice.code), voice]),
    );
    for (const voiceCode of options.recommendedVoiceCodes) {
      const voice = candidatesByCode.get(normalizeVoiceCode(voiceCode));
      if (voice) return voice;
    }
    return null;
  }
  return (
    voices
      .filter((voice) => {
        if (!voice.recommended) return false;
        if (options?.vendor && voice.vendor !== options.vendor) return false;
        return !excluded || voice.code !== excluded;
      })
      .sort(
        (left, right) =>
          (left.recommendationOrder ?? Number.MAX_SAFE_INTEGER) -
          (right.recommendationOrder ?? Number.MAX_SAFE_INTEGER),
      )[0] ?? null
  );
}

export function pickDefaultTtsCatalogVoice(
  voices: readonly VideoEditorTTSVoice[],
  options?: PickDefaultTtsCatalogVoiceOptions,
): VideoEditorTTSVoice | null {
  const excluded = options?.excludeCode?.trim();
  const candidates = voices.filter((voice) => {
    if (options?.vendor && voice.vendor !== options.vendor) return false;
    return !excluded || voice.code !== excluded;
  });

  const preferredCodes = (options?.preferredCodes ?? []).map(
    normalizeVoiceCode,
  );
  const preferredVoice = candidates.find((voice) =>
    preferredCodes.includes(normalizeVoiceCode(voice.code)),
  );
  if (preferredVoice) return preferredVoice;

  // BE `/tts/voices` 의 defaultUseCases 가 기본 보이스 정책의 SoT다.
  // defaultUseCases 가 없는 구 catalog 응답은 배포 순서 하위 호환을 위해서만 기존 순서 fallback을 사용한다.
  const useCase = normalizeUseCase(
    options?.useCase === undefined
      ? DEFAULT_TTS_CATALOG_USE_CASE
      : options.useCase,
  );
  const useCaseDefaultVoice = useCase
    ? candidates.find((voice) =>
        (voice.defaultUseCases ?? []).includes(useCase),
      )
    : null;
  if (useCaseDefaultVoice) return useCaseDefaultVoice;
  if (options?.allowLegacyOrderFallback === false) return null;

  return (
    candidates[0] ?? voices.find((voice) => voice.code !== excluded) ?? null
  );
}

export function pickDefaultTtsCatalogVoiceCode(
  voices: readonly VideoEditorTTSVoice[],
  options?: PickDefaultTtsCatalogVoiceOptions,
): string | null {
  return pickDefaultTtsCatalogVoice(voices, options)?.code ?? null;
}

function normalizeVoiceCode(value: string | null | undefined): string {
  return value?.trim().toLowerCase() ?? '';
}

function normalizeUseCase(value: string | null | undefined): string {
  return value?.trim() ?? '';
}
